import * as THREE from 'three';
import { PlanogramPosition } from "./PlanogramPosition";
export class PlanogramPositionCap extends HTMLElement{

    get isTray() { return this.type === 'tray' || this.type === 'case' || this.type === 'pallet'; }
    get isCase() { return this.type === 'case'; }
    get isPallet() { return this.type === 'pallet'; }

    static CapAttributes = {
        X: 'x',
        Y: 'y',
        Z: 'z',
        RotationX: 'rotation-x',
        RotationY: 'rotation-y',
        RotationZ: 'rotation-z',
        Wide : 'wide',
        High : 'high',
        Deep : 'deep',
        UnitWide : 'unit-wide',
        UnitHigh : 'unit-high',
        UnitDeep : 'unit-deep',
        TrayWidth : 'tray-width',
        TrayHeight : 'tray-height',
        TrayDepth : 'tray-depth',
        FacingSpaceX : 'facing-space-x',
        FacingSpaceY : 'facing-space-y',
        FacingSpaceZ : 'facing-space-z',
        Width : 'width',
        Height : 'height',
        Depth : 'depth',
        Type : 'type'
    }  

    static get observedAttributes(){
		return [ ...Object.values(PlanogramPositionCap.CapAttributes)];
	}
    
    #parentPosition = null;

    //#region Attributes
    get x() { return Number(this.getAttribute(PlanogramPositionCap.CapAttributes.X)) || 0; }
    get y() { return Number(this.getAttribute(PlanogramPositionCap.CapAttributes.Y)) || 0; }
    get z() { return Number(this.getAttribute(PlanogramPositionCap.CapAttributes.Z)) || 0; }
    get rotationX() { return Number(this.getAttribute(PlanogramPositionCap.CapAttributes.RotationX)) || 0; }
    get rotationY() { return Number(this.getAttribute(PlanogramPositionCap.CapAttributes.RotationY)) || 0; }
    get rotationZ() { return Number(this.getAttribute(PlanogramPositionCap.CapAttributes.RotationZ)) || 0; }
    get wide() { return Number(this.getAttribute(PlanogramPositionCap.CapAttributes.Wide)) || 1; }
    get high() { return Number(this.getAttribute(PlanogramPositionCap.CapAttributes.High)) || 1; }
    get deep() { return Number(this.getAttribute(PlanogramPositionCap.CapAttributes.Deep)) || 1; }
    get unitWide() { return Number(this.getAttribute(PlanogramPositionCap.CapAttributes.UnitWide)) || 1; }
    get unitHigh() { return Number(this.getAttribute(PlanogramPositionCap.CapAttributes.UnitHigh)) || 1; }
    get unitDeep() { return Number(this.getAttribute(PlanogramPositionCap.CapAttributes.UnitDeep)) || 1; }
    get trayWidth() { return Number(this.getAttribute(PlanogramPositionCap.CapAttributes.TrayWidth)) || 0; }
    get trayHeight() { return Number(this.getAttribute(PlanogramPositionCap.CapAttributes.TrayHeight)) || 0; }
    get trayDepth() { return Number(this.getAttribute(PlanogramPositionCap.CapAttributes.TrayDepth)) || 0; }
    get type() { return this.getAttribute(PlanogramPositionCap.CapAttributes.Type); }
    get facingSpaceX() { return Number(this.getAttribute(PlanogramPositionCap.CapAttributes.FacingSpaceX)) || 0; }
    get facingSpaceY() { return Number(this.getAttribute(PlanogramPositionCap.CapAttributes.FacingSpaceY)) || 0; }
    get facingSpaceZ() { return Number(this.getAttribute(PlanogramPositionCap.CapAttributes.FacingSpaceZ)) || 0; }
    get width() { return Number(this.getAttribute(PlanogramPositionCap.CapAttributes.Width)) || 1; }
    get height() { return Number(this.getAttribute(PlanogramPositionCap.CapAttributes.Height)) || 1; }
    get depth() { return Number(this.getAttribute(PlanogramPositionCap.CapAttributes.Depth)) || 1; }
    //#endregion

    //#region Private Properties
    #firstRender = true;
    //#endregion

    get isMultipleX(){ return this.isTray || this.facingSpaceX > 0 ; }
    get isMultipleY(){ return this.isTray || this.facingSpaceY > 0 ; }
    get isMultipleZ(){ return this.isTray || this.facingSpaceZ > 0 ; }

    get valueX(){ return ( this.isMultipleX ? this.wide : 1 ); }
    get valueY(){ return ( this.isMultipleY ? this.high : 1 ); }
    get valueZ(){ return ( this.isMultipleZ ? this.deep : 1 ); }

    get multiplierX(){return this.isTray && this.facingSpaceX === 0 ? this.wide : this.unitWide;}
    get multiplierY(){return this.isTray && this.facingSpaceY === 0 ? this.high : this.unitHigh;}
    get multiplierZ(){return this.isTray && this.facingSpaceZ === 0 ? this.deep : this.unitDeep;}

    get totalWidth(){ return this.isMultipleX ? ( this.width + this.facingSpaceX ) * this.wide : this.width;}
    get totalHeight(){ return this.isMultipleY ? ( this.height + this.facingSpaceY ) * this.high : this.height;}
    get totalDepth(){ return this.isMultipleZ ? ( this.depth + this.facingSpaceZ ) * this.deep : this.depth;}

    get facingsX(){ return this.isTray ? this.unitWide : this.facingSpaceX === 0 ? this.wide : 1 }
    get facingsY(){ return this.isTray ? this.unitHigh : this.facingSpaceY === 0 ? this.high : 1}
    get facingsZ(){ return this.isTray ? this.unitDeep : this.facingSpaceZ === 0 ? this.deep : 1}

    get sizeX(){ return this.isTray ? this.width / this.facingsX : this.width; }
    get sizeY(){ return this.isTray ? this.height / this.facingsY : this.height; }
    get sizeZ(){ return this.isTray ? this.depth / this.facingsZ : this.depth; }

    get meshCount(){ return (this.valueX * this.valueY * this.valueZ) << this.isTray; }

    constructor() { super(); }

    imagePlug(){
        if(this.isConnected){
            this.parentElement.imagePlug();
        }
    }

    getVectors(){
        return [...Array(this.valueX)].flatMap( (a,x) => [...Array(this.valueY)].
            flatMap((b, y) => [...Array(this.valueZ)].flatMap((c, z)=> [...Array( 1 << this.isTray )].map( () => new THREE.Vector3(
            ((( this.width + this.facingSpaceX ) * x) + this.facingSpaceX/2  - this.totalWidth/2) + this.width/2, 
            ((( this.height + this.facingSpaceY ) * y) + this.facingSpaceY/2  - this.totalHeight/2) + this.height/2,
            ((( this.depth + this.facingSpaceZ ) * z) + this.facingSpaceZ/2  - this.totalDepth/2) + this.depth/2 )
                .applyEuler(new THREE.Euler(this.rotationX, this.rotationY, this.rotationZ))))));
    }

    getTrayValue(){
        if(this.isCase)return 3;
        if(this.isPallet)return 4;
        if(this.isTray) return 2;
        return 0;
    }

    getImageAttributes(){
        const sides = { 
            right: -1, 
            left: -1, 
            top: -1, 
            bottom: -1, 
            front: -1, 
            back: -1 
        };
        
        [...this.querySelectorAll("planogram-image")].filter(x=> x.depth || x.depth === 0).forEach(image => {
            if(Reflect.has(sides, image.placement))
                sides[image.placement] = image.depth;
        });
        return this.isTray ? 
        { 
            rltArray: [ sides.right, sides.left, sides.top, -1, -1, -1 ], 
            bfbkArray: [ sides.bottom, sides.front, sides.back, -1, -1, -1] 
        }: 
        { 
            rltArray: [ sides.right, sides.left, sides.top ], 
            bfbkArray: [ sides.bottom, sides.front, sides.back] 
        };


    }

    connectedCallback() {
        if(!(this.parentElement instanceof PlanogramPosition))
            this.remove();
        this.#parentPosition = this.parentElement;
        this.#parentPosition.capAvailable();
        this.#firstRender = false;
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (this.#firstRender || oldValue === newValue) return;
        this.#parentPosition.capUpdated();
    }

    disconnectedCallback(){
        this.#parentPosition.capRemoved();
    }
}